import React, { useCallback, useEffect, useState } from "react";
import { useLocation, useParams } from "react-router-dom";
import { useDispatch } from "react-redux";
import { Calendar, momentLocalizer } from "react-big-calendar";
import withDragAndDrop from "react-big-calendar/lib/addons/dragAndDrop";
import "react-big-calendar/lib/addons/dragAndDrop/styles.css";
import "react-big-calendar/lib/css/react-big-calendar.css";
import moment from "moment";
import "moment/locale/ja";

import {
  StyledAppCalendar,
  CalendarLengthButton,
  CalendarLengthButtonGroup,
  StyledDivider,
  InstrumentTitleSection,
} from "./instrumentCalendarStyles";
import { AppIconButton, CircularLoader } from "@components";
import { ArrowLeftIcon, ArrowRightIcon } from "@assets";
import { updateSnackBar } from "@redux";
import { instrumentApi } from "@api";
import { calculateReservationsInMonth } from "src/Utils/CalculateRecurringBorrowings";
import { appRouteEnums } from "@enums";
import { useAppNavigation } from "@utils";
import { domInstrumentTimeHeaderSticky } from "./domInstrumentTimeHeaderSticky";

// Configure moment to use Japanese locale
moment.locale("ja");

// Create moment localizer for react-big-calendar
const localizer = momentLocalizer(moment);

// Create drag and drop calendar component (for future use)
const DragAndDropCalendar = withDragAndDrop(Calendar);

type CalendarSpan = "month" | "week" | "day";

export const InstrumentCalendarComponent = () => {
  const { id } = useParams<{ id: string }>();
  const location = useLocation();
  const params = new URLSearchParams(location.search);
  const queryDate = params.get("date") || undefined;

  // Instrument data state
  const [calendarData, setCalendarData] = useState<any>();
  const [loading, setLoading] = useState<boolean>(false);
  const dispatch = useDispatch();
  const { navigatePreservingQuery } = useAppNavigation();

  const navigateToInstrumentDetail = useCallback(
    (params?: Record<string, any>) => {
      if (!id) { return; }
      console.log("Navigating to instrument detail with params:", params);
      navigatePreservingQuery(
        `${appRouteEnums.PATH_INSTRUMENTS}/${id}`,
        params,
        "push"
      );
    },
    [id, navigatePreservingQuery]
  );

  const handleStickyAddNavigate = useCallback(
    (instrumentId: string, date: string) => {
      console.log("Handling sticky add navigate with instrumentId:", instrumentId, "and date:", date);
      navigatePreservingQuery(
        `${appRouteEnums.PATH_INSTRUMENTS}/${instrumentId}`,
        { start: date },
        "push"
      );
    },
    [navigatePreservingQuery]
  );

  // Calendar view state
  const [calendarSpan, setCalendarSpan] = useState<CalendarSpan>(queryDate ? "day" : "month");
  const [currentDate, setCurrentDate] = useState<Date>(queryDate ? new Date(queryDate) : new Date());
  const month = moment(currentDate).format("YYYY-MM");
  const [isMobile, setIsMobile] = useState(() =>
    window.matchMedia("(max-width: 767px)").matches
  );

  const [eventsInMonth, setEventsInMonth] = useState<any[]>([]);
  const [currentEventId, setCurrentEventId] = useState<number | null>(null);
  const [currentEventStart, setCurrentEventStart] = useState<string | null>(null);
  const [showSelectEffectRangeModal, setShowSelectEffectRangeModal] = useState<boolean>(false);

  // Ref for headerDummyIndividualWeekly
  const headerDummyIndividualWeeklyRef = React.useRef<HTMLDivElement>(null);

  // Agenda view support
  const agendaView = (calendarSpan === 'month' && isMobile);
  const firstDayOfMonth = new Date(currentDate.getFullYear(), currentDate.getMonth(), 1);
  const monthLength = new Date(currentDate.getFullYear(), currentDate.getMonth() + 1, 0).getDate();


  const runReservationCalculation = (month: string) => {
    if (!calendarData) { return; }
    const reservationsInMonth = calculateReservationsInMonth(month, calendarData);
    //console.log("Reservations in month:", reservationsInMonth);
    const events = reservationsInMonth.map((reservation, idx) => {
      //console.log("Processing reservation:", reservation);
      const isRecurring = reservation.isRecurring;
      const startDate = reservation.date;
      const endDate = reservation.endDate ? reservation.endDate : reservation.date;

      const isAllDay = !reservation.fromTime || !reservation.toTime;
      const isSingleDay = startDate === endDate;

      const eventStartTime = isAllDay ? moment(reservation.date).startOf("day").toDate() : moment(`${reservation.date}T${reservation.fromTime}`).toDate();
      const eventEndTime = isAllDay ? moment(endDate).endOf("day").toDate() : moment(`${endDate}T${reservation.toTime}`).toDate();

      const title =
        (isSingleDay && isAllDay)  ? "一日中" :
        (isSingleDay && !isAllDay) ? `${moment(eventStartTime).format("H:mm")} 〜 ${moment(eventEndTime).format("H:mm")} ` :
        (!isSingleDay && isAllDay) ? `${moment(startDate).format("M/D")}〜${moment(endDate).format("M/D")} (一日中)` :
        (!isSingleDay && !isAllDay) ? `${moment(startDate).format("M/D")} ${moment(eventStartTime).format("H:mm")} 〜 ${moment(endDate).format("M/D")} ${moment(eventEndTime).format("H:mm")}` :
        "Unexpected title";

      const isFinished = !moment().isAfter(eventEndTime);
      const isReturned = reservation.isReturned ? true : false;
      const isLate = reservation.isLate ? true : false;
      const backgroundColor = isReturned ? "#A9A9A9" : isLate ? "#FF0000" : isFinished ? "#3174AD" : "#487399";
      return {
        id: idx,
        parentId: reservation.parentId,
        title: (isRecurring ? "⟳ " : "") + title,
        start: eventStartTime,
        end: eventEndTime,
        isReturned: isReturned,
        isRecurring: isRecurring,
        isEditable: isFinished,
        isLate: isLate,
        backgroundColor: backgroundColor,
        borrowerName: reservation.borrowerName,
        comment: reservation.comment,
        isModification: reservation.modificationId ? true : false,
      };
    });

    events.sort((a, b) => a.start.getTime() - b.start.getTime());

    setEventsInMonth(events);
  };

  useEffect(() => {
    runReservationCalculation(month);
  }, [month, calendarData]);

  const openInstrumentDetailWithRange = (effectRange: string) => {
    const currentEvent = eventsInMonth.find(event => event.id === currentEventId);
    navigateToInstrumentDetail({
      itemId: currentEventId ?? undefined,
      startDate: currentEventStart ? moment(currentEventStart).format("YYYY-MM-DD") : undefined,
      effectRange,
      reservationId: currentEvent?.parentId || currentEventId || undefined,
      //start: moment(currentEvent?.start).format("YYYY-MM-DDTHH:mm:ss"),
      startTime: currentEventStart ? moment(currentEventStart).format("HH:mm:ss") : undefined,
    });
  };

  const cancelRecurring = async (effectRange: string) => {
    setLoading(true);
    const currentEvent = calendarData.history.find(event => event.id === currentEventId);

    if (!currentEvent) {
      console.error("No current event found for cancellation. currentEventId: ", currentEventId);
      setLoading(false);
      return;
    }

    const reservationId = currentEvent?.parentId || currentEventId || undefined;
    console.log("Cancelling reservationId:", reservationId, "for event:", currentEvent);

    // This doesn't reference an existing reservation. Can't cancel what you can't find. Error,
    if (!reservationId) {
      console.error("No reservationId found for cancellation.");
      setLoading(false);
      return;
    }

    try {
      const apiPayload = {
        reservationId: reservationId,
        oldStart: currentEventStart ? `${moment(currentEventStart).format("YYYY-MM-DD")} ${moment(currentEventStart).format("HH:mm:ss")}` : `${moment(currentEventStart).format("YYYY-MM-DD")} 00:00:00`,
        isCancelled: true,
      };

      // cancel this and all future reservation occurrences
      if (effectRange === "future") {
        console.log("Going to cancel this and future occurrences with payload:", apiPayload);
        const res = await instrumentApi.instrumentRecurring(apiPayload, id);  // id is instrument id
        if (res && res.status !== 200) {
          console.error("Failed to cancel future reservations:", res);
        }
      }

      // cancel only this reservation occurrence
      else if (effectRange === "this") {
        console.log("Going to cancel only this occurrence with payload:", apiPayload);
        const res = await instrumentApi.instrumentModify(apiPayload, id);  // id is instrument id
        if (res && res.status !== 200) {
          console.error("Failed to cancel this reservation occurrence:", res);
        }
      }

      else {
        console.error("Invalid effectRange value:", effectRange);
      }

      fetchCalendarData(id);
      setLoading(false);
      setShowSelectEffectRangeModal(false);
    } catch (error: any) {
      setLoading(false);
    }
  };

  const handleDragOrResize = async (event: any, start: Date, end: Date) => {
    if (event.isRecurring) { alert("定期的な予約はドラッグで変更できません。"); return; }
    if (event.isReturned || event.isLate || !event.isEditable) return;

    const historyItem = calendarData?.history?.find((h: any) => h.id === event.parentId);

    setLoading(true);
    try {
      await instrumentApi.borrowInstrument({
        reservationId: event.parentId,
        oldStart: `${moment(event.start).format("YYYY-MM-DD")} ${moment(event.start).format("HH:mm:ss")}`,
        newStart: `${moment(start).format("YYYY-MM-DD")} ${moment(start).format("HH:mm")}:00`,
        newEnd: `${moment(end).format("YYYY-MM-DD")} ${moment(end).format("HH:mm")}:00`,
        from: moment(start).format("YYYY-MM-DD"),
        to: moment(end).format("YYYY-MM-DD"),
        fromTime: moment(start).format("HH:mm"),
        toTime: moment(end).format("HH:mm"),
        quantity: 1,
        construction_id: historyItem?.construction_id || null,
        comment: historyItem?.comment || "",
      }, id);
      fetchCalendarData(id);
    } catch (error: any) {
      const msg = error?.response?.data?.message;
      dispatch(updateSnackBar({ messageType: "error", message: (typeof msg === "string" ? msg : null) || "予約の変更に失敗しました。" }));
      setLoading(false);
    }
  };

  const fetchCalendarData = async (instrumentId: string | number) => {
    setLoading(true);
    try {
      const response = await instrumentApi.getInstrumentCalendar({
        id: instrumentId,
        onlyActive: false,
      });
      setLoading(false);
      setCalendarData(response.data.data);
    } catch (error: any) {
      setLoading(false);
      dispatch(
        updateSnackBar({
          messageType: "error",
          message: error.response.data.message,
        })
      );
    }
  };

  // Fetch instrument calendar data
  useEffect(() => {
    if (id) {
      fetchCalendarData(id);
    }
  }, [id, dispatch]);

  // Mobile detection effect
  useEffect(() => {
    const mediaQuery = window.matchMedia("(max-width: 767px)");
    const handler = (e: MediaQueryListEvent) => setIsMobile(e.matches);
    mediaQuery.addEventListener("change", handler);
    return () => mediaQuery.removeEventListener("change", handler);
  }, []);

  // Reset scroll on view change
  useEffect(() => {
    const el = document.querySelector("#instrumentCalendarContainer");
    if (el) {
      el.scrollTop = 0;
      el.scrollLeft = 0;
    }
  }, [calendarSpan, currentDate]);

  // Make time header sticky in week/day view
  useEffect(() => {
    const cleanup = domInstrumentTimeHeaderSticky(calendarSpan, handleStickyAddNavigate);
    return cleanup;
  }, [calendarSpan, currentDate, eventsInMonth, handleStickyAddNavigate]);

  // Filter and hide events outside the current month in agenda view
  useEffect(() => {
    if (agendaView) {
      const agendaTableBody = document.querySelector('.rbc-agenda-table tbody');
      if (agendaTableBody) {
        const currentViewMonth = moment(currentDate).month();
        let currentGroupIsOutOfMonth = false;

        const rows = agendaTableBody.querySelectorAll('tr');
        rows.forEach(row => {
          const trElement = row as HTMLElement;
          let spanWithDate: Element | null = null;

          const dateCell = trElement.querySelector('td.rbc-agenda-date-cell');
          if (dateCell) {
            spanWithDate = dateCell.querySelector('span[data-date]');
          }

          if (!spanWithDate) {
            spanWithDate = trElement.querySelector('span[data-date]');
          }

          if (spanWithDate) {
            const dateStr = spanWithDate.getAttribute('data-date');
            if (dateStr) {
              const rowDate = moment(dateStr, 'YYYY-MM-DD');
              if (rowDate.isValid() && rowDate.month() !== currentViewMonth) {
                currentGroupIsOutOfMonth = true;
                trElement.style.display = 'none';
              } else if (rowDate.isValid()) {
                currentGroupIsOutOfMonth = false;
                trElement.style.display = '';
              } else {
                trElement.style.display = currentGroupIsOutOfMonth ? 'none' : '';
              }
            } else {
              trElement.style.display = currentGroupIsOutOfMonth ? 'none' : '';
            }
          } else {
            trElement.style.display = currentGroupIsOutOfMonth ? 'none' : '';
          }
        });
      }
    } else {
      const agendaTableBody = document.querySelector('.rbc-agenda-table tbody');
      if (agendaTableBody) {
        agendaTableBody.querySelectorAll('tr').forEach(row => {
          (row as HTMLElement).style.display = '';
        });
      }
    }

    return () => {
      const agendaTableBody = document.querySelector('.rbc-agenda-table tbody');
      if (agendaTableBody) {
        agendaTableBody.querySelectorAll('tr').forEach(row => {
          (row as HTMLElement).style.display = '';
        });
      }
    };
  }, [agendaView, currentDate, eventsInMonth, isMobile, calendarSpan]);

  // Scroll to today in agenda view
  useEffect(() => {
    if (!agendaView) return;

    const scrollToToday = () => {
      const currentMonth = moment(currentDate).format('YYYY-MM');
      const todayMonth = moment().format('YYYY-MM');
      const isCurrentMonth = currentMonth === todayMonth;

      let scrollableContainer: HTMLElement | null = null;

      const agendaTable = document.querySelector('.rbc-agenda-table');
      if (agendaTable) {
        let currentElement: HTMLElement | null = agendaTable as HTMLElement;

        while (currentElement && !scrollableContainer) {
          currentElement = currentElement.parentElement;
          if (currentElement) {
            const style = window.getComputedStyle(currentElement);
            const overflow = style.overflow + style.overflowY;

            if (overflow.includes('scroll') || overflow.includes('auto')) {
              if (currentElement.scrollHeight > currentElement.clientHeight) {
                scrollableContainer = currentElement;
              }
            }
          }
        }
      }

      if (!scrollableContainer) {
        const fallbackContainers = [
          document.getElementById('instrumentCalendarContainer'),
          document.querySelector('[id^="instrumentCalendarContainer"]'),
          document.querySelector('.rbc-agenda-view')?.parentElement
        ];

        for (const container of fallbackContainers) {
          if (container && container instanceof HTMLElement && container.scrollHeight > container.clientHeight) {
            scrollableContainer = container;
            break;
          }
        }
      }

      if (!scrollableContainer) return;

      if (!isCurrentMonth) {
        scrollableContainer.scrollTop = 0;
        return;
      }

      const dateCells = document.querySelectorAll('span[data-date]');
      if (dateCells.length === 0) return;

      const todayStr = moment().format('YYYY-MM-DD');
      let todayRow: HTMLElement | null = null;

      for (let i = 0; i < dateCells.length; i++) {
        const cell = dateCells[i];
        if (cell.getAttribute('data-date') === todayStr) {
          let parent: HTMLElement | null = cell as HTMLElement;
          while (parent && parent.tagName !== 'TR') {
            parent = parent.parentElement;
          }
          if (parent) {
            todayRow = parent;
            break;
          }
        }
      }

      if (!todayRow) {
        scrollableContainer.scrollTop = 0;
        return;
      }

      try {
        void todayRow.offsetHeight;
        void scrollableContainer.offsetHeight;

        const rowRect = todayRow.getBoundingClientRect();
        const containerRect = scrollableContainer.getBoundingClientRect();

        const targetScroll = scrollableContainer.scrollTop + (rowRect.top - containerRect.top) - 0;
        scrollableContainer.scrollTop = targetScroll;

        setTimeout(() => {
          const newRowRect = todayRow.getBoundingClientRect();
          const newContainerRect = scrollableContainer.getBoundingClientRect();
          const newTargetScroll = scrollableContainer.scrollTop + (newRowRect.top - newContainerRect.top) - 0;

          if (Math.abs(newTargetScroll - scrollableContainer.scrollTop) > 5) {
            scrollableContainer.scrollTop = newTargetScroll;
          }
        }, 100);
      } catch (error) {
        console.error('[Agenda Scroll] Error:', error);
      }
    };

    const resetAndScroll = () => {
      const container = document.getElementById('instrumentCalendarContainer');
      if (container) {
        container.scrollTop = 0;
        setTimeout(scrollToToday, 50);
      } else {
        scrollToToday();
      }
    };

    const timeout1 = setTimeout(resetAndScroll, 100);
    const timeout2 = setTimeout(scrollToToday, 300);
    const timeout3 = setTimeout(scrollToToday, 500);

    return () => {
      clearTimeout(timeout1);
      clearTimeout(timeout2);
      clearTimeout(timeout3);
    };
  }, [agendaView, currentDate, eventsInMonth]);

  // headerDummyIndividualWeekly effect - manages visibility and height in weekly view
  useEffect(() => {
    const el = headerDummyIndividualWeeklyRef.current;
    if (!el) return;

    const isHeaderDummyActiveByCalendar = calendarSpan === 'week';

    // Find the scrollable container and the .rbc-row-content element
    const scrollContainer = document.querySelector('#instrumentCalendarContainer') as HTMLElement | null;
    const rowContent = document.querySelector('.rbc-row-content') as HTMLElement | null;

    if (!isHeaderDummyActiveByCalendar || !rowContent || !scrollContainer) {
      el.style.display = "none";
      el.style.height = '0px';
      return;
    }

    el.style.display = "block";

    // Function to update visible height
    const updateHeight = () => {
      const containerRect = scrollContainer.getBoundingClientRect();
      const rowRect = rowContent.getBoundingClientRect();

      // Calculate intersection
      const visibleTop = Math.max(containerRect.top, rowRect.top);
      const visibleBottom = Math.min(containerRect.bottom, rowRect.bottom);
      const visibleHeight = Math.max(0, visibleBottom - visibleTop);

      el.style.height = `${visibleHeight + 26}px`;
    };

    // Initial update
    updateHeight();

    // Listen for scroll and resize
    scrollContainer.addEventListener('scroll', updateHeight);
    window.addEventListener('resize', updateHeight);

    // Clean up
    return () => {
      scrollContainer.removeEventListener('scroll', updateHeight);
      window.removeEventListener('resize', updateHeight);
    };
  }, [calendarSpan, currentDate]);

  // Navigation handlers
  const goToBack = () => {
    let newDate = new Date(currentDate);
    if (calendarSpan === "month") {
      const targetMonth = newDate.getMonth() - 1;
      const targetYear = newDate.getFullYear();
      if (targetMonth < 0) {
        newDate = new Date(targetYear - 1, 11, 1);
      } else {
        newDate = new Date(targetYear, targetMonth, 1);
      }
    } else if (calendarSpan === "week") {
      newDate.setDate(newDate.getDate() - 7);
    } else if (calendarSpan === "day") {
      newDate.setDate(newDate.getDate() - 1);
    }
    setCurrentDate(newDate);
  };

  const goToNext = () => {
    let newDate = new Date(currentDate);
    if (calendarSpan === "month") {
      const targetMonth = newDate.getMonth() + 1;
      const targetYear = newDate.getFullYear();
      if (targetMonth > 11) {
        newDate = new Date(targetYear + 1, 0, 1);
      } else {
        newDate = new Date(targetYear, targetMonth, 1);
      }
    } else if (calendarSpan === "week") {
      newDate.setDate(newDate.getDate() + 7);
    } else if (calendarSpan === "day") {
      newDate.setDate(newDate.getDate() + 1);
    }
    setCurrentDate(newDate);
  };

  const goToCurrent = () => {
    setCurrentDate(new Date());
  };

  // Date display text
  let currentDateText = "";
  if (calendarSpan === "month") {
    currentDateText = moment(currentDate).format("YYYY年 M月");
  } else if (calendarSpan === "week") {
    const weekStart = moment(currentDate).startOf("week");
    const weekEnd = moment(currentDate).endOf("week");

    if (
      weekStart.year() === weekEnd.year() &&
      weekStart.month() === weekEnd.month()
    ) {
      currentDateText = `${weekStart.format("YYYY年 M月 D日")}〜${weekEnd.format("D日")}`;
    } else if (weekStart.year() === weekEnd.year()) {
      currentDateText = `${weekStart.format("YYYY年 M月 D日")}〜${weekEnd.format("M月 D日")}`;
    } else {
      currentDateText = `${weekStart.format("YYYY/M/D")}〜${weekEnd.format("YYYY/M/D")}`;
    }
  } else {
    currentDateText = moment(currentDate).format("YYYY年 M月 D日");
  }

  const todayLabel =
    calendarSpan === "day"
      ? "今日"
      : calendarSpan === "week"
        ? "今週"
        : "今月";

  // Custom date header component to show today indicator and add button
  const CustomDateHeader = ({ date }: { date: Date; label: string }) => {
    const isToday = moment(date).isSame(new Date(), "day");

    const handleAddClick = (e: React.MouseEvent) => {
      e.stopPropagation(); // prevent triggering other calendar clicks
      console.log("Add clicked for", date);

      const formattedDate = moment(date).format('YYYY-MM-DD');
      navigateToInstrumentDetail({ start: formattedDate });
    };

    return (
      <div style={{ position: "relative", minHeight: "30px" }}>
        <span className={isToday ? "date-is-today" : ""}>
          {date.getDate()}
        </span>
        <button
          onClick={handleAddClick}
          style={{
            position: "absolute",
            top: 5,
            left: 5,
            zIndex: 10,
            background: "transparent",
            color: "#ff6a0c",
            border: "none",
            borderRadius: "50%",
            width: "18px",
            height: "18px",
            fontSize: "24px",
            fontWeight: "bold",
            lineHeight: "14px",
            cursor: "pointer",
          }}
        >
          +
        </button>
      </div>
    );
  };

  // Custom week date header for week/day views
  const CustomWeekDateHeader = ({ date }: { date: Date; label: string }) => {
    const isToday = moment(date).isSame(new Date(), "day");

    // Format the label to show month/day and Japanese day of week (same as ScheduleCalendar)
    const formattedLabel = `${moment(date).format('M/D')} (${moment(date).format('dd')})`;

    const handleAddClick = (e: React.MouseEvent) => {
      e.stopPropagation(); // prevent triggering other calendar clicks
      console.log("Add clicked for", date);

      const formattedDate = moment(date).format('YYYY-MM-DD');
      navigateToInstrumentDetail({ start: formattedDate });
    };

    return (
      <div style={{
        position: "relative",
        minHeight: "30px",
        width: "100%",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        flexShrink: 1,
        flexBasis: "100%",
        fontWeight: "bold",
      }}>
        <span className={isToday ? "date-is-today-header" : ""}>{formattedLabel}</span>
        <div
          onClick={handleAddClick}
          data-date={moment(date).format('YYYY-MM-DD')}
          data-instrument-id={id}
          className="instrument-calendar-add-button"
          style={{
            position: "absolute",
            left: 5,
            top: 7,
            zIndex: 10,
            background: "transparent",
            color: "#ff6a0c",
            border: "none",
            borderRadius: "50%",
            width: "18px",
            height: "18px",
            fontSize: "24px",
            fontWeight: "bold",
            lineHeight: "14px",
            cursor: "pointer",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            pointerEvents: "auto",
          }}
        >
          +
        </div>
      </div>
    );
  };

  // Blank toolbar (we use our own controls)
  const blankToolbar = () => null;

  // Blank header for week/day views
  const blankHeader = () => null;

  // Style for each day cell - matches ScheduleCalendar
  const dayPropGetter = (date: Date) => {
    const day = date.getDay(); // 0 = Sunday, 6 = Saturday

    let backgroundColor = "white";

    // Weekend styling
    if (day === 6 || day === 0) {
      backgroundColor = "#f3f3f3"; // Saturday & Sunday
    }

    return {
      style: {
        backgroundColor,
      },
    };
  };

  const eventPropGetter = (event: any) => {
    const style = {
      backgroundColor: event.backgroundColor,
      border: "0px",
      // borderRadius: "5px",
      // opacity: 0.8,
      // color: "white",
      // display: "block",
    };
    return {
      style: style,
    };
  };


  // Custom agenda date component
  const CustomAgendaDate = (props: any) => {
    const day = props.day || props.date || props.value || props;
    const fullDate = moment(day).format("YYYY-MM-DD");
    const isToday = moment(day).isSame(moment(), 'day');

    const dayNumStr = moment(day).locale("ja").format("DD");
    const hasLeadingZero = dayNumStr.startsWith("0");
    const displayDayNum = hasLeadingZero ? (
      <>
        <span style={{ color: "white", fontWeight: "normal" }}>0</span>
        <span style={{ color: isToday ? "red" : undefined, fontWeight: isToday ? "bold" : "normal" }}>{dayNumStr[1]}</span>
      </>
    ) : (
      <span style={{ color: isToday ? "red" : undefined, fontWeight: isToday ? "bold" : "normal" }}>{dayNumStr}</span>
    );

    const weekday = moment(day).locale("ja").format("dd");

    const handleAddClick = (e: React.MouseEvent) => {
      e.stopPropagation();
      const formattedDate = moment(day).format('YYYY-MM-DD');
      navigateToInstrumentDetail({ start: formattedDate });
    };

    return (
      <span data-date={fullDate}>
        <span style={{ fontWeight: "normal", fontSize: "14px" }}>
          <button
            onClick={handleAddClick}
            style={{
              position: "relative",
              top: 3,
              left: 0,
              zIndex: 10,
              background: "transparent",
              color: "#ff6a0c",
              border: "none",
              borderRadius: "50%",
              width: "23px",
              height: "18px",
              fontSize: "24px",
              fontWeight: "bold",
              lineHeight: "14px",
              cursor: "pointer",
              textAlign: "left"
            }}
          >
            +
          </button>
          {displayDayNum}
        </span>
        <span style={{ fontWeight: "normal", fontSize: "14px", color: "darkgray", marginLeft: "6px" }}>
          {weekday}
        </span>
      </span>
    );
  };

  // Custom agenda event component
  const CustomAgendaEvent = (props: any) => {
    const { event } = props;

    const stripAlpha = (hex: string) => {
      if (/^#([0-9a-fA-F]{8})$/.test(hex)) {
        return '#' + hex.slice(1, 7);
      }
      return hex;
    };

    let backgroundColor = event.backgroundColor || "#3174AD";
    backgroundColor = stripAlpha(backgroundColor);

    if (event.emptyEvent) {
      return <div style={{}} />;
    }

    return (
      <div
        style={{
          background: backgroundColor,
          borderRadius: "6px",
          opacity: 1,
          border: "0px",
          fontSize: "12px",
          boxSizing: "border-box",
          display: "flex",
          flexDirection: "column",
          alignItems: "flex-start",
          padding: "4px 7px",
          textAlign: "left",
          maxWidth: "100%",
          cursor: "pointer",
          position: "relative",
          zIndex: 2,
        }}
      >
        <div style={{ display: "flex", flexDirection: "row", alignItems: "center" }}>
          <span style={{ fontWeight: "bold", display: "flex", alignItems: "center", color: "white" }}>
            {event.isRecurring && "⟳ "}
            {event.title}
          </span>
        </div>
      </div>
    );
  };

  // Adds "なし" events for dates with no events in the current month
  const addPlaceholderEvents = (sparseEvents: any[]) => {
    const datesWithEvents = new Map<string, boolean>();

    // Mark dates that already have events (including all days for multi-day events)
    sparseEvents.forEach(event => {
      const startDate = new Date(event.start);
      const endDate = new Date(event.end);

      startDate.setHours(0, 0, 0, 0);
      endDate.setHours(0, 0, 0, 0);

      const currentDay = new Date(startDate);
      while (currentDay <= endDate) {
        const dateStr = `${currentDay.getFullYear()}-${currentDay.getMonth() + 1}-${currentDay.getDate()}`;
        datesWithEvents.set(dateStr, true);
        currentDay.setDate(currentDay.getDate() + 1);
      }
    });

    const updatedEvents = [...sparseEvents];
    const day = new Date(firstDayOfMonth);
    const lastDayOfMonth = new Date(currentDate.getFullYear(), currentDate.getMonth() + 1, 0);

    while (day <= lastDayOfMonth) {
      const dateStr = `${day.getFullYear()}-${day.getMonth() + 1}-${day.getDate()}`;

      if (!datesWithEvents.has(dateStr)) {
        const currentDay = new Date(day);
        updatedEvents.push({
          start: currentDay,
          end: currentDay,
          title: "なし",
          backgroundColor: "#ffffff88",
          emptyEvent: true,
        });
      }
      day.setDate(day.getDate() + 1);
    }

    return updatedEvents;
  };

  // Apply placeholder events for agenda view
  const displayEvents = agendaView ? addPlaceholderEvents(eventsInMonth) : eventsInMonth;

  const selectEffectRange = (
    <div style={{position: "absolute", zIndex: 20, display: showSelectEffectRangeModal ? "flex" : "none", alignItems: "center", justifyContent: "center", top: 0, bottom: 0, left: 0, right: 0, backgroundColor: "#000000aa", padding: "1rem"}}>
      <div style={{borderRadius: "0.5rem", overflow: "hidden", boxShadow: "0 0 1rem #00000055", width: "100%", maxWidth: "400px"}}>
        <span style={{display: "flex", backgroundColor: "#ddd", color: "black", fontWeight: "bold", padding: "0.5rem 1rem", width: "100%"}}>定期的な予約の編集</span>
        <div style={{backgroundColor: "white", display: "flex", flexDirection: "column", alignItems: "stretch", padding: "1rem", gap: "0.75rem"}}>
          <button style={{ backgroundColor: "#ff6a0c", color: "white", border: "none", borderRadius: "50px", cursor: "pointer", fontSize: "12px", fontWeight: "bold", padding: "1rem 2rem", height: "40px", textAlign: "center" }} className="EffectRangeButton" onClick={() => openInstrumentDetailWithRange("this")}>この予約のみを編集</button>
          <button style={{ backgroundColor: "#ff6a0c", color: "white", border: "none", borderRadius: "50px", cursor: "pointer", fontSize: "12px", fontWeight: "bold", padding: "1rem 2rem", height: "40px", textAlign: "center" }} className="EffectRangeButton" onClick={() => openInstrumentDetailWithRange("future")}>これ以降の予約を編集</button>
          <button style={{ backgroundColor: "#ff6a0c", color: "white", border: "none", borderRadius: "50px", cursor: "pointer", fontSize: "12px", fontWeight: "bold", padding: "1rem 2rem", height: "40px", textAlign: "center" }} className="EffectRangeButton" onClick={() => openInstrumentDetailWithRange("all")}>すべての予約を編集</button>
          <button style={{ backgroundColor: "#ff6a0c", color: "white", border: "none", borderRadius: "50px", cursor: "pointer", fontSize: "12px", fontWeight: "bold", padding: "1rem 2rem", height: "40px", textAlign: "center" }} className="EffectRangeButton" onClick={() => cancelRecurring("this")}>この日の予約をキャンセルします</button>
          <button style={{ backgroundColor: "#ff6a0c", color: "white", border: "none", borderRadius: "50px", cursor: "pointer", fontSize: "12px", fontWeight: "bold", padding: "1rem 2rem", height: "40px", textAlign: "center" }} className="EffectRangeButton" onClick={() => cancelRecurring("future")}>これ以降の予約をキャンセルします</button>
          <button style={{ backgroundColor: "#cccccc", color: "#666", border: "none", borderRadius: "50px", cursor: "pointer", fontSize: "12px", fontWeight: "bold", padding: "1rem 2rem", height: "40px", textAlign: "center" }} className="EffectRangeButton" onClick={() => {setShowSelectEffectRangeModal(false); setCurrentEventId(null); setCurrentEventStart(null);}}>Close</button>
        </div>
      </div>
    </div>
  );


  return (
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        height: isMobile ? "100svh" : "100vh",
        background: "#f5f5f5",
        overflow: "hidden",
        paddingTop: isMobile ? "7rem" : "8rem",
        paddingBottom: isMobile ? "0.5rem" : "1.5rem",
        paddingLeft: isMobile ? "0.5rem" : "1.5rem",
        paddingRight: isMobile ? "0.5rem" : "1.5rem",
      }}
    >
      <CircularLoader loading={loading} />
      {selectEffectRange}

      {/* Header Section */}
      <div
        style={{
          display: "flex",
          flexDirection: "column",
          alignItems: "flex-start",
          justifyContent: "flex-start",
          width: "100%",
        }}
      >
        {/* Title */}
        <span
          style={{
            fontSize: isMobile ? "3rem" : "4rem",
            fontWeight: "bold",
          }}
        >
          機器
        </span>

        <StyledDivider />

        {/* Instrument Name/Title */}
        {calendarData && (
          <InstrumentTitleSection>
            <p className="main">{calendarData.title}</p>
            {calendarData.category && (
              <p className="subTitle">{calendarData.category}</p>
            )}
          </InstrumentTitleSection>
        )}

        {/* View Toggle Buttons */}
        <CalendarLengthButtonGroup>
          <CalendarLengthButton
            className="calendar-length-button"
            selected={calendarSpan === "month"}
            onClick={() => setCalendarSpan("month")}
          >
            月
          </CalendarLengthButton>
          <CalendarLengthButton
            className="calendar-length-button"
            selected={calendarSpan === "week"}
            onClick={() => setCalendarSpan("week")}
          >
            週
          </CalendarLengthButton>
          <CalendarLengthButton
            className="calendar-length-button"
            selected={calendarSpan === "day"}
            onClick={() => setCalendarSpan("day")}
          >
            日
          </CalendarLengthButton>
        </CalendarLengthButtonGroup>

        {/* Date Navigation Bar */}
        <div
          style={{
            display: "flex",
            flexDirection: "row",
            justifyContent: "space-between",
            alignItems: "center",
            width: "100%",
            marginTop: "0.5rem",
          }}
        >
          <span
            style={{
              fontSize: isMobile ? "16px" : "20px",
              display: "flex",
              alignItems: "center",
              fontWeight: "bold",
            }}
          >
            {currentDateText}
          </span>
          <div style={{ display: "flex", alignItems: "center" }}>
            <div className="btn-back">
              <AppIconButton onClick={goToBack} icon={<ArrowLeftIcon />} />
            </div>
            <label
              className="btn-current"
              onClick={goToCurrent}
              style={{
                cursor: "pointer",
                fontSize: "11px",
                fontWeight: "bold",
                margin: "0 1rem",
                backgroundColor: "#666",
                borderRadius: "6px",
                color: "white",
                padding: "3px 4px",
              }}
            >
              {todayLabel}
            </label>
            <div className="btn-next">
              <AppIconButton onClick={goToNext} icon={<ArrowRightIcon />} />
            </div>
          </div>
        </div>
      </div>

      {/* Calendar Container */}
      <div
        id="instrumentCalendarContainer"
        style={{
          display: "flex",
          flexDirection: "column",
          width: "100%",
          flex: 1,
          minHeight: 0,
          overflow: "auto",
          border: "2px solid white",
          borderRadius: "10px",
          marginTop: "0.5rem",
          overscrollBehavior: "none",
          position: "relative",
        }}
      >
        <div
          style={{
            display: "flex",
            flex: "1",
            flexDirection: "row",
            alignItems: "start",
            minWidth: calendarSpan === "week" ? "1000px" : "200px",
            width: "100%",
            height: "100%",
          }}
        >
          <div id="headerDummyIndividualWeekly"
            ref={headerDummyIndividualWeeklyRef}
            style={{
              position: "fixed",
              zIndex: 30,
              flexShrink: 0,
              width: "46px",
              minWidth: "46px",
              height: "0px",
              display: "none",
              background: "#e4e4e4",
              borderRight: "1px solid #bbb"
            }}
          />
          <StyledAppCalendar $span={calendarSpan}>
            <DragAndDropCalendar
            localizer={localizer}
            events={displayEvents}
            onSelectEvent={(event: any, e: React.SyntheticEvent) => {
              // Don't handle clicks on empty events
              if (event.emptyEvent) return;

              // Prevent any parent <Link> or onClick from firing
              if (e && typeof e.preventDefault === "function") e.preventDefault();
              if (e && typeof (e as any).stopPropagation === "function") (e as any).stopPropagation();
              console.log("Clicked event:", event);
              const isFinished = !moment().isAfter(event.end);
              if (event.isRecurring && isFinished) {
                setCurrentEventId(event.parentId);
                setCurrentEventStart(moment(event.start).format("YYYY-MM-DDTHH:mm:ss"));
                setShowSelectEffectRangeModal(true);
              } else {
                const navigateParams = {
                    reservationId: event.parentId,
                    start: moment(event.start).format("YYYY-MM-DDTHH:mm:ss"),
                    effectRange: event.isModification ? "this" : undefined,
                    startDate: moment(event.start).format("YYYY-MM-DD"),
                    startTime: moment(event.start).format("HH:mm:ss"),
                    endDate: moment(event.end).format("YYYY-MM-DD"),
                    endTime: moment(event.end).format("HH:mm:ss"),
                  };
                console.log(`Navigating to instrument detail which is at ${appRouteEnums.PATH_INSTRUMENTS}/${id} for instrument id ${id} with params:`, navigateParams);
                navigatePreservingQuery(`${appRouteEnums.PATH_INSTRUMENTS}/${id}`, navigateParams, "push");
              }
            }}
            onSelectSlot={(slotInfo: any) => {
              console.log("Slot selected:", slotInfo);

              const startMoment = moment(slotInfo.start);
              const endMoment = moment(slotInfo.end);

              const dateStr = startMoment.format("YYYY-MM-DD");
              const startTimeStr = startMoment.format("HH:mm:ss");
              const endTimeStr = endMoment.format("HH:mm:ss");

              // Detect if this is a time-based selection (mainly for week/day view)
              const isTimeSelection =
                (calendarSpan === "day" || calendarSpan === "week") &&
                endMoment.isAfter(startMoment);

              if (isTimeSelection) {
                // With timespan: pass date + startTime + endTime
              navigateToInstrumentDetail({
                start: dateStr,
                startTime: startTimeStr,
                endTime: endTimeStr,
              });
              } else {
                // Month view (or simple date click): keep current behavior
              navigateToInstrumentDetail({ start: dateStr });
              }
            }}
            tooltipAccessor={(event: any) => event.borrowerName}
            views={["month", "week", "day", "agenda"]}
            view={agendaView ? "agenda" : calendarSpan}
            date={agendaView ? firstDayOfMonth : currentDate}
            length={monthLength}
            onNavigate={(date) => setCurrentDate(date)}
            dayPropGetter={dayPropGetter}
            eventPropGetter={eventPropGetter}
            components={{
              toolbar: blankToolbar,
              header: calendarSpan === "month" ? undefined : blankHeader,
              month: {
                dateHeader: CustomDateHeader,
              },
              week: {
                header: CustomWeekDateHeader,
              },
              day: {
                header: CustomWeekDateHeader,
              },
              agenda: {
                date: CustomAgendaDate,
                event: CustomAgendaEvent,
              },
            }}
            selectable={true}
            resizable
            draggableAccessor={(event: any) => !event.emptyEvent && !event.isReturned && !event.isLate && !event.isRecurring && event.isEditable}
            onEventDrop={({ event, start, end }: any) => handleDragOrResize(event, start, end)}
            onEventResize={({ event, start, end }: any) => handleDragOrResize(event, start, end)}
            style={{ height: "100%", width: "100%" }}
            messages={{
              today: "今日",
              previous: "前",
              next: "次",
              month: "月",
              week: "週",
              day: "日",
              agenda: "アジェンダ",
              date: "日付",
              time: "時間",
              event: "イベント",
              noEventsInRange: "この期間にイベントはありません",
              showMore: (total) => `+${total} 件`,
            }}
          />
        </StyledAppCalendar>
        </div>
      </div>
    </div>
  );
};
